/**
 * Map View utilities.
 *
 * Toggle between list view and map view in search results.
 * Stores user preference in localStorage.
 *
 * Map view: search results pinned on an interactive map.
 * Uses Mapbox (configurable via REACT_APP_MAPBOX_ACCESS_TOKEN) or
 * Google Maps (REACT_APP_GOOGLE_MAPS_API_KEY).
 */

export const VIEW_MODES = ['list', 'map', 'split'];
export const DEFAULT_VIEW_MODE = 'list';
export const STORAGE_KEY = 'ith:searchViewMode';

/**
 * Get user's preferred view mode from localStorage.
 * @returns {string}
 */
export const getSavedViewMode = () => {
  if (typeof localStorage === 'undefined') return DEFAULT_VIEW_MODE;
  const saved = localStorage.getItem(STORAGE_KEY);
  return VIEW_MODES.includes(saved) ? saved : DEFAULT_VIEW_MODE;
};

/**
 * Save view mode preference to localStorage.
 * @param {string} mode
 */
export const saveViewMode = (mode) => {
  if (typeof localStorage === 'undefined') return;
  if (VIEW_MODES.includes(mode)) localStorage.setItem(STORAGE_KEY, mode);
};

/**
 * Convert listings to GeoJSON FeatureCollection for map display.
 * @param {Object[]} listings
 * @returns {Object} GeoJSON FeatureCollection
 */
export const listingsToGeoJSON = (listings) => {
  if (!Array.isArray(listings)) return { type: 'FeatureCollection', features: [] };

  const features = listings
    .filter(l => {
      const geo = l?.attributes?.geolocation;
      return geo?.lat !== undefined && geo?.lng !== undefined;
    })
    .map(l => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [l.attributes.geolocation.lng, l.attributes.geolocation.lat],
      },
      properties: {
        id: l.id?.uuid,
        title: l.attributes.title,
        price: l.attributes.price?.amount,
        currency: l.attributes.price?.currency,
      },
    }));

  return { type: 'FeatureCollection', features };
};

/**
 * Calculate map bounds from listings.
 * @param {Object[]} listings
 * @returns {{ ne: { lat, lng }, sw: { lat, lng } }|null}
 */
export const calculateBounds = (listings) => {
  if (!Array.isArray(listings) || !listings.length) return null;

  const geoPoints = listings
    .map(l => l?.attributes?.geolocation)
    .filter(g => g?.lat !== undefined && g?.lng !== undefined);

  if (!geoPoints.length) return null;

  const lats = geoPoints.map(g => g.lat);
  const lngs = geoPoints.map(g => g.lng);

  return {
    ne: { lat: Math.max(...lats), lng: Math.max(...lngs) },
    sw: { lat: Math.min(...lats), lng: Math.min(...lngs) },
  };
};
